'use client';

import { useMemo, useState } from 'react';

import { CallOffOrderColor, FlatQuotationLine, QuotedColor, QuotedColorGroup, SmallLotPricing } from '@ravago/shared/page-data/models/elements';

import { useSearchParams } from 'next/navigation';

import {
   CallOffGroup,
   flattenQuotedColorGroup,
   groupCallOffColors,
   groupQuotedColors,
   groupSmallLotPrices,
   makeColorGroupKey,
   resolveUrlInitialKeys,
   SmallLotGroup,
   SmallLotUrlSelection
} from '../utils/order-now-utils';

import { useSmallLotPricingState } from './use-small-lot-pricing-state';

export type OrderNowV2Step = 'color-selector' | 'call-off-detail' | 'quote-delivery-address-selection' | 'quote-detail' | 'small-lot-price-view';

export interface QuoteDeliveryAddressOption {
   id: number;
   name: string;
   formattedAddress: string;
}

export const getQuoteDeliveryAddressOptions = (selectedColorGroup: QuotedColorGroup | undefined): QuoteDeliveryAddressOption[] => {
   if (!selectedColorGroup) return [];

   const addresses = new Map<number, QuoteDeliveryAddressOption>();

   for (const quotedColor of selectedColorGroup.quotedColors) {
      const address = quotedColor.quotation.deliveryInformation?.address;
      if (!address) continue;

      if (!addresses.has(address.id)) {
         addresses.set(address.id, {
            id: address.id,
            name: address.name,
            formattedAddress: address.rawFormat
         });
      }
   }

   return Array.from(addresses.values());
};

export const filterQuotedColorGroupByDeliveryAddress = (
   selectedColorGroup: QuotedColorGroup | undefined,
   selectedDeliveryAddressId: number | undefined
): QuotedColorGroup | undefined => {
   if (!selectedColorGroup || selectedDeliveryAddressId === undefined) return selectedColorGroup;

   return {
      ...selectedColorGroup,
      quotedColors: selectedColorGroup.quotedColors.filter(
         (quotedColor) => quotedColor.quotation.deliveryInformation?.address?.id === selectedDeliveryAddressId
      )
   };
};

const resolveUniqueVariantColorCode = (smallLotGroups: SmallLotGroup[], colorId: number): string | undefined => {
   const matches = smallLotGroups.filter((group) => group.colorId === colorId);
   return matches.length === 1 ? matches[0].producerColorCode : undefined;
};

/**
 * Filters the tiers of a SmallLotPricing entry based on a matching QuotedColorGroup.
 * Only tiers whose quantityTo is strictly less than the minimum quoted quantity are kept.
 * Returns null when all tiers are filtered out (entry should be excluded entirely).
 * Returns the original pricing unchanged when no matching quote group exists.
 */
export const filterSmallLotTiersByQuote = (pricing: SmallLotPricing, matchingQuoteGroup: QuotedColorGroup | undefined): SmallLotPricing | null => {
   if (!matchingQuoteGroup) return pricing;

   const allLineAmounts = matchingQuoteGroup.quotedColors.flatMap((qc) => qc.quotation.lines.map((l) => l.quantity.amount));
   const minQuoteQuantity = Math.min(...allLineAmounts);

   const visibleTiers = pricing.tiers.filter((t) => t.quantityTo.amount < minQuoteQuantity);
   return visibleTiers.length > 0 ? { ...pricing, tiers: visibleTiers } : null;
};

export const deriveInitialCallOffKey = (
   urlCallOffKey: string | undefined,
   urlQuoteKey: string | undefined,
   callOffGroups: CallOffGroup[]
): string | undefined => {
   if (urlCallOffKey) return urlCallOffKey;
   if (!urlQuoteKey && callOffGroups.length === 1) return callOffGroups[0].key;
   return undefined;
};

export const deriveInitialQuoteKey = (
   urlQuoteKey: string | undefined,
   urlCallOffKey: string | undefined,
   quotedColorGroups: QuotedColorGroup[]
): string | undefined => {
   if (urlQuoteKey) return urlQuoteKey;
   if (!urlCallOffKey && quotedColorGroups.length === 1) return quotedColorGroups[0].key;
   return undefined;
};

interface DeriveInitialStepParams {
   initialSelectedQuoteKey: string | undefined;
   urlQuoteKey: string | undefined;
   urlCallOffKey: string | undefined;
   smallLotUrlSelection: SmallLotUrlSelection | undefined;
   callOffGroups: CallOffGroup[];
   quotedColorGroups: QuotedColorGroup[];
   smallLotGroups: SmallLotGroup[];
   callOffOrderColorsCount: number;
   quotedColorsCount: number;
}

const resolveQuoteDetailStep = (hasMultipleDeliveryAddressesForQuote: boolean): OrderNowV2Step =>
   hasMultipleDeliveryAddressesForQuote ? 'quote-delivery-address-selection' : 'quote-detail';

const hasMatchingSmallLotSelection = (smallLotUrlSelection: SmallLotUrlSelection, smallLotGroups: SmallLotGroup[]): boolean => {
   const { colorId, colorCode } = smallLotUrlSelection;
   const resolvedColorCode = colorCode ?? resolveUniqueVariantColorCode(smallLotGroups, colorId);

   return smallLotGroups.some((group) => group.key === makeColorGroupKey(colorId, resolvedColorCode));
};

const deriveImplicitInitialStep = ({
   callOffOrderColorsCount,
   quotedColorsCount,
   smallLotGroupsCount,
   callOffGroupsCount,
   quotedColorGroupsCount,
   uniqueSmallLotColorsCount,
   hasMultipleDeliveryAddressesForQuote
}: {
   callOffOrderColorsCount: number;
   quotedColorsCount: number;
   smallLotGroupsCount: number;
   callOffGroupsCount: number;
   quotedColorGroupsCount: number;
   uniqueSmallLotColorsCount: number;
   hasMultipleDeliveryAddressesForQuote: boolean;
}): OrderNowV2Step => {
   if (callOffOrderColorsCount === 0 && quotedColorsCount === 0 && smallLotGroupsCount === 1) return 'small-lot-price-view';
   if (callOffGroupsCount === 1 && quotedColorGroupsCount === 0 && uniqueSmallLotColorsCount === 0) return 'call-off-detail';
   if (quotedColorGroupsCount === 1 && callOffGroupsCount === 0 && uniqueSmallLotColorsCount === 0) {
      return resolveQuoteDetailStep(hasMultipleDeliveryAddressesForQuote);
   }

   return 'color-selector';
};

export const deriveInitialStep = ({
   initialSelectedQuoteKey,
   urlQuoteKey,
   urlCallOffKey,
   smallLotUrlSelection,
   callOffGroups,
   quotedColorGroups,
   smallLotGroups,
   callOffOrderColorsCount,
   quotedColorsCount
}: DeriveInitialStepParams): OrderNowV2Step => {
   const selectedQuoteGroup = quotedColorGroups.find((g) => g.key === initialSelectedQuoteKey);
   const hasMultipleDeliveryAddressesForQuote = getQuoteDeliveryAddressOptions(selectedQuoteGroup).length > 1;

   if (urlQuoteKey && quotedColorGroups.some((g) => g.key === urlQuoteKey)) {
      return resolveQuoteDetailStep(hasMultipleDeliveryAddressesForQuote);
   }

   if (urlCallOffKey && callOffGroups.some((g) => g.key === urlCallOffKey)) return 'call-off-detail';

   if (smallLotUrlSelection && hasMatchingSmallLotSelection(smallLotUrlSelection, smallLotGroups)) return 'small-lot-price-view';

   return deriveImplicitInitialStep({
      callOffOrderColorsCount,
      quotedColorsCount,
      smallLotGroupsCount: smallLotGroups.length,
      callOffGroupsCount: callOffGroups.length,
      quotedColorGroupsCount: quotedColorGroups.length,
      uniqueSmallLotColorsCount: new Set(smallLotGroups.map((group) => group.colorId)).size,
      hasMultipleDeliveryAddressesForQuote
   });
};

interface Props {
   callOffOrderColors: CallOffOrderColor[];
   quotedColors: QuotedColor[];
   smallLotPrices: SmallLotPricing[];
   bracketPricingEnabled?: boolean;
}

export const useOrderNowV2State = ({ callOffOrderColors, quotedColors, smallLotPrices, bracketPricingEnabled = false }: Props) => {
   const searchParams = useSearchParams();

   const callOffGroups = useMemo(() => groupCallOffColors(callOffOrderColors), [callOffOrderColors]);
   const quotedColorGroups = useMemo(() => groupQuotedColors(quotedColors), [quotedColors]);

   const filteredSmallLotPrices = useMemo(
      () =>
         smallLotPrices.filter(
            (smallLotPrice) =>
               smallLotPrice.color.name.trim().length > 0 &&
               !quotedColorGroups.some((g) => g.key === makeColorGroupKey(smallLotPrice.color.id, smallLotPrice.color.producerColorCode)) &&
               !callOffGroups.some((g) => g.key === makeColorGroupKey(smallLotPrice.color.id, smallLotPrice.color.producerColorCode))
         ),
      [smallLotPrices, quotedColorGroups, callOffGroups]
   );

   const [{ callOffKey: urlCallOffKey, quoteKey: urlQuoteKey, smallLotUrlSelection }] = useState(() =>
      resolveUrlInitialKeys(searchParams, callOffGroups, quotedColorGroups)
   );

   const smallLotGroups = useMemo(() => groupSmallLotPrices(filteredSmallLotPrices), [filteredSmallLotPrices]);
   const uniqueSmallLotColorsCount = useMemo(() => new Set(smallLotGroups.map((group) => group.colorId)).size, [smallLotGroups]);

   const {
      uniqueSmallLotColors,
      variantsForSelectedColor: colorCodesForSelectedColor,
      selectedColorId: selectedSmallLotColorId,
      selectedProducerColorCode: selectedSmallLotProducerColorCode,
      selectedPricing,
      handleColorChange,
      handleProducerColorCodeChange: handleSmallLotColorCodeChange,
      clearSelection: clearSmallLotSelection
   } = useSmallLotPricingState({
      smallLotPrices: filteredSmallLotPrices,
      autoPreselect: callOffOrderColors.length === 0 && quotedColors.length === 0 && uniqueSmallLotColorsCount === 1,
      urlSelection: smallLotUrlSelection
   });

   const initialCallOffKey =
      selectedSmallLotColorId !== undefined ? undefined : deriveInitialCallOffKey(urlCallOffKey, urlQuoteKey, callOffGroups);
   const initialQuoteKey =
      selectedSmallLotColorId !== undefined || initialCallOffKey !== undefined
         ? undefined
         : deriveInitialQuoteKey(urlQuoteKey, urlCallOffKey, quotedColorGroups);

   const [selectedCallOffKey, setSelectedCallOffKey] = useState<string | undefined>(initialCallOffKey);

   const [selectedColorGroupKey, setSelectedColorGroupKey] = useState<string | undefined>(initialQuoteKey);

   const selectedCallOffGroup = useMemo(() => callOffGroups.find((g) => g.key === selectedCallOffKey), [callOffGroups, selectedCallOffKey]);

   const selectedColorGroup = useMemo(
      () => quotedColorGroups.find((g) => g.key === selectedColorGroupKey),
      [quotedColorGroups, selectedColorGroupKey]
   );

   const quoteDeliveryAddressOptions = useMemo(() => getQuoteDeliveryAddressOptions(selectedColorGroup), [selectedColorGroup]);

   const [selectedDeliveryAddressId, setSelectedDeliveryAddressId] = useState<number | undefined>(() => {
      const initialSelectedQuoteGroup = quotedColorGroups.find((g) => g.key === initialQuoteKey);
      const initialAddresses = getQuoteDeliveryAddressOptions(initialSelectedQuoteGroup);
      return initialAddresses.length === 1 ? initialAddresses[0].id : undefined;
   });

   const filteredSelectedColorGroup = useMemo(
      () => filterQuotedColorGroupByDeliveryAddress(selectedColorGroup, selectedDeliveryAddressId),
      [selectedColorGroup, selectedDeliveryAddressId]
   );

   const flatLines = useMemo((): FlatQuotationLine[] => {
      if (!filteredSelectedColorGroup) return [];
      if (quoteDeliveryAddressOptions.length > 1 && selectedDeliveryAddressId === undefined) return [];
      return flattenQuotedColorGroup(filteredSelectedColorGroup, { bracketPricingEnabled });
   }, [filteredSelectedColorGroup, quoteDeliveryAddressOptions, selectedDeliveryAddressId, bracketPricingEnabled]);

   const smallLotPricingForSelectedColorGroup = useMemo(() => {
      if (!selectedColorGroup) return null;
      const rawPricing =
         smallLotPrices.find(
            (smallLotPrice) => makeColorGroupKey(smallLotPrice.color.id, smallLotPrice.color.producerColorCode) === selectedColorGroup.key
         ) ?? null;
      if (!rawPricing) return null;
      return filterSmallLotTiersByQuote(rawPricing, selectedColorGroup);
   }, [selectedColorGroup, smallLotPrices]);

   const hasMultipleSelections = quotedColorGroups.length + callOffGroups.length + smallLotGroups.length > 1;

   const [step, setStep] = useState<OrderNowV2Step>(() =>
      deriveInitialStep({
         initialSelectedQuoteKey: initialQuoteKey,
         urlQuoteKey,
         urlCallOffKey,
         smallLotUrlSelection,
         callOffGroups,
         quotedColorGroups,
         smallLotGroups,
         callOffOrderColorsCount: callOffOrderColors.length,
         quotedColorsCount: quotedColors.length
      })
   );

   const handleCallOffGroupSelect = (key: string) => {
      setSelectedCallOffKey(key);
      setSelectedColorGroupKey(undefined);
      setSelectedDeliveryAddressId(undefined);
      clearSmallLotSelection();
   };

   const handleQuotedColorGroupSelect = (key: string) => {
      setSelectedColorGroupKey(key);
      setSelectedCallOffKey(undefined);
      setSelectedDeliveryAddressId(undefined);
      clearSmallLotSelection();
   };

   const handleSmallLotColorChange = (colorId: number) => {
      handleColorChange(colorId);
      setSelectedCallOffKey(undefined);
      setSelectedColorGroupKey(undefined);
      setSelectedDeliveryAddressId(undefined);
   };

   const handleViewCallOff = () => setStep('call-off-detail');
   const handleViewQuote = () => {
      if (quoteDeliveryAddressOptions.length > 1) {
         setSelectedDeliveryAddressId(undefined);
         setStep('quote-delivery-address-selection');
         return;
      }

      if (quoteDeliveryAddressOptions.length === 1) {
         setSelectedDeliveryAddressId(quoteDeliveryAddressOptions[0].id);
      }

      setStep('quote-detail');
   };

   const handleSelectDeliveryAddress = (deliveryAddressId: number) => setSelectedDeliveryAddressId(deliveryAddressId);

   const handleContinueQuoteWithDeliveryAddress = () => {
      if (selectedDeliveryAddressId === undefined) return;
      setStep('quote-detail');
   };
   const handleViewPrices = () => setStep('small-lot-price-view');
   const handleEditClick = () => setStep('color-selector');

   return {
      callOffGroups,
      quotedColorGroups,
      filteredSmallLotPrices,
      uniqueSmallLotColors,
      colorCodesForSelectedColor,
      selectedCallOffKey,
      selectedColorGroupKey,
      selectedSmallLotColorId,
      selectedSmallLotProducerColorCode,
      selectedCallOffGroup,
      selectedColorGroup,
      selectedPricing,
      selectedDeliveryAddressId,
      flatLines,
      quoteDeliveryAddressOptions,
      smallLotPricingForSelectedColorGroup,
      hasMultipleSelections,
      step,
      handleCallOffGroupSelect,
      handleQuotedColorGroupSelect,
      handleSmallLotColorChange,
      handleSmallLotColorCodeChange,
      handleViewCallOff,
      handleViewQuote,
      handleSelectDeliveryAddress,
      handleContinueQuoteWithDeliveryAddress,
      handleViewPrices,
      handleEditClick
   };
};
